/**
 * Auth Selectors
 * Memoized selectors for auth state
 */

import { createSelector } from "@reduxjs/toolkit";
import type { RootState } from "@/store/types";

// Base selectors
export const userDetails = (state: RootState) => state.auth;

// Redux-persist rehydration selector
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const selectIsRehydrated = (state: RootState) => (state as any)._persist?.rehydrated ?? true;

// Memoized selectors
export const selectIsAuthenticated = createSelector(
  userDetails,
  (auth) => auth.isAuthenticated
);

export const selectAccessToken = createSelector(
  userDetails,
  (auth) => auth.accessToken
);

export const selectLoginResponse = createSelector(
  userDetails,
  (auth) => auth.loginResponse
);

export const selectUserDetails = createSelector(
  selectLoginResponse,
  (loginResponse) => loginResponse?.loginUserDetails || null
);

export const selectUserRole = createSelector(
  selectUserDetails,
  (userDetails) => userDetails?.role || "user"
);

export const selectEmailRefId = createSelector(
  userDetails,
  (auth) => auth.emailRefId
);

export const selectPhoneRefId = createSelector(
  userDetails,
  (auth) => auth.phoneRefId
);


export const selectCIN = createSelector(
  selectUserDetails,
  (userDetails) => userDetails?.ciNo || null
);

// ============================================
// Pending Auth Selectors (New Login Flow)
// Must be defined before selectUserEmail which depends on them
// ============================================

export const selectPendingAuth = createSelector(
  userDetails,
  (auth) => auth.pendingAuth
);

export const selectPendingAuthEmail = createSelector(
  selectPendingAuth,
  (pendingAuth) => pendingAuth?.email || null
);

export const selectPendingAuthRefreshToken = createSelector(
  selectPendingAuth,
  (pendingAuth) => pendingAuth?.refreshToken || null
);

export const selectHasPendingAuth = createSelector(
  selectPendingAuth,
  (pendingAuth) => pendingAuth !== null
);

// User email selector - checks both loginUserDetails and pendingAuth
export const selectUserEmail = createSelector(
  [selectUserDetails, selectPendingAuth],
  (userDetails, pendingAuth) => userDetails?.emailId || pendingAuth?.email || null
);

export const selectUserName = createSelector(
  selectUserDetails,
  (userDetails) =>
    userDetails
      ? `${userDetails.firstName} ${userDetails.lastName}`.trim()
      : null
);

export const selectUserId = createSelector(
  selectUserDetails,
  (userDetails) => userDetails?.id || null
);

// ============================================
// WBA (WhatsApp Business Account) Selectors
// ============================================

export const selectWBADetails = createSelector(
  userDetails,
  (auth) => auth.wbaDetails
);

export const selectWBAPhoneNumbers = createSelector(
  selectWBADetails,
  (wbaDetails) => wbaDetails?.wba_phone_numbers || []
);

export const selectSelectedWBAPhoneNumber = createSelector(
  userDetails,
  (auth) => auth.selectedWBAPhoneNumber
);

export const selectSelectedWBAPhoneNoId = createSelector(
  selectSelectedWBAPhoneNumber,
  (phoneNumber) => phoneNumber?.wba_phone_no_id || null
);

export const selectSelectedWBAPhoneNo = createSelector(
  selectSelectedWBAPhoneNumber,
  (phoneNumber) => phoneNumber?.wba_phone_no || null
);

export const selectWBAId = createSelector(
  selectWBADetails,
  (wbaDetails) => wbaDetails?.wba_id || null
);

export const selectWBAAppId = createSelector(
  selectWBADetails,
  (wbaDetails) => wbaDetails?.wbaAppId || null
);

export const selectHasMultipleWBAPhones = createSelector(
  selectWBAPhoneNumbers,
  (phoneNumbers) => phoneNumbers.length > 1
);

// ============================================
// Organization Selectors
// ============================================

export const selectOrganizations = createSelector(
  userDetails,
  (auth) => auth.organizations ?? []
);

export const selectOrganizationsCount = createSelector(
  selectOrganizations,
  (organizations) => organizations?.length ?? 0
);

export const selectSelectedOrganization = createSelector(
  userDetails,
  (auth) => auth.selectedOrganization
);

export const selectSelectedOrganizationCIN = createSelector(
  selectSelectedOrganization,
  (organization) => organization?.cin || null
);

export const selectSelectedOrganizationName = createSelector(
  selectSelectedOrganization,
  (organization) => organization?.businessName || null
);

export const selectSelectedOrganizationAgentStatus = createSelector(
  selectSelectedOrganization,
  (organization) => organization?.agentStatus || null
);

export const selectIsAgentActive = createSelector(
  selectSelectedOrganizationAgentStatus,
  (agentStatus) => agentStatus === "ACTIVE"
);

export const selectIsAgentInactive = createSelector(
  selectSelectedOrganizationAgentStatus,
  (agentStatus) => agentStatus === "INACTIVE"
);

export const selectIsAgentDeleted = createSelector(
  selectSelectedOrganizationAgentStatus,
  (agentStatus) => agentStatus === "DELETED"
);

/**
 * Returns true if the agent is restricted (INACTIVE or DELETED).
 * Used by route guards to prevent access to protected pages.
 */
export const selectIsAgentRestricted = createSelector(
  selectSelectedOrganizationAgentStatus,
  (agentStatus) => agentStatus === "INACTIVE" || agentStatus === "DELETED"
);

export const selectOrganizationsLoading = createSelector(
  userDetails,
  (auth) => auth.organizationsLoading
);

export const selectOrganizationsError = createSelector(
  userDetails,
  (auth) => auth.organizationsError
);

export const selectHasMultipleOrganizations = createSelector(
  selectOrganizations,
  (organizations) => (organizations?.length ?? 0) > 1
);

export const selectRequiresOrganizationSelection = createSelector(
  userDetails,
  (auth) => auth.requiresOrganizationSelection
);

// ============================================
// Project Selectors
// ============================================

export const selectProjects = createSelector(
  userDetails,
  (auth) => auth.projects ?? []
);

export const selectProjectsCount = createSelector(
  selectProjects,
  (projects) => projects?.length ?? 0
);

export const selectSelectedProject = createSelector(
  userDetails,
  (auth) => auth.selectedProject
);

export const selectSelectedProjectId = createSelector(
  selectSelectedProject,
  (project) => project?.projectId || null
);

export const selectSelectedProjectKey = createSelector(
  selectSelectedProject,
  (project) => project?.projectKey || null
);

export const selectSelectedProjectName = createSelector(
  selectSelectedProject,
  (project) => project?.projectName || null
);

export const selectSelectedProjectRoleName = createSelector(
  selectSelectedProject,
  (project) => project?.roles?.roleName || null
);

export const selectSelectedProjectRoleId = createSelector(
  selectSelectedProject,
  (project) => project?.roles?.roleId ?? null
);

export const selectProjectsLoading = createSelector(
  userDetails,
  (auth) => auth.projectsLoading
);

export const selectProjectsError = createSelector(
  userDetails,
  (auth) => auth.projectsError
);

export const selectHasMultipleProjects = createSelector(
  selectProjects,
  (projects) => (projects?.length ?? 0) > 1
);

export const selectRequiresProjectSelection = createSelector(
  userDetails,
  (auth) => auth.requiresProjectSelection
);

export const selectDefaultProject = createSelector(
  selectProjects,
  (projects) => projects?.find((p) => p.isDefault) || null
);

/**
 * Selector to check if user has no projects available
 * Returns true when:
 * - Projects have been loaded (not loading, no error)
 * - Projects array is empty
 * - Organization is selected (projects should have been fetched)
 */
export const selectHasNoProjectsAvailable = createSelector(
  [
    selectProjects,
    selectProjectsLoading,
    selectProjectsError,
    selectSelectedOrganization,
  ],
  (projects, isLoading, error, selectedOrg) => {
    // Only return true if we've finished loading projects for a selected org
    // and there are no projects available
    return (
      selectedOrg !== null &&
      !isLoading &&
      !error &&
      projects.length === 0
    );
  }
);

// ============================================
// Login Refresh Token Selector
// ============================================

export const selectLoginRefreshToken = createSelector(
  selectLoginResponse,
  (loginResponse) => loginResponse?.refreshToken || null
);

// ============================================
// Project Token Selectors
// ============================================

export const selectProjectAccessToken = createSelector(
  userDetails,
  (auth) => auth.projectAccessToken
);

export const selectProjectRefreshToken = createSelector(
  userDetails,
  (auth) => auth.projectRefreshToken
);

export const selectHasProjectTokens = createSelector(
  [selectProjectAccessToken, selectProjectRefreshToken],
  (accessToken, refreshToken) => accessToken !== null && refreshToken !== null
);

/**
 * Returns the effective access token for API/socket authorization.
 * Mirrors the base-query logic: projectAccessToken ?? accessToken
 */
export const selectEffectiveAccessToken = createSelector(
  [selectProjectAccessToken, selectAccessToken],
  (projectToken, loginToken) => projectToken ?? loginToken
);

// ============================================
// Flow Control Selectors
// ============================================

export const selectIsSessionComplete = createSelector(
  [
    selectIsAuthenticated,
    selectSelectedOrganization,
    selectSelectedProject,
    selectRequiresOrganizationSelection,
    selectRequiresProjectSelection,
  ],
  (
    isAuthenticated,
    selectedOrg,
    selectedProj,
    requiresOrgSelection,
    requiresProjSelection
  ) =>
    isAuthenticated &&
    selectedOrg !== null &&
    selectedProj !== null &&
    !requiresOrgSelection &&
    !requiresProjSelection
);

export const selectSessionSetupStep = createSelector(
  [
    selectIsAuthenticated,
    selectHasPendingAuth,
    selectRequiresOrganizationSelection,
    selectRequiresProjectSelection,
    selectSelectedOrganization,
    selectSelectedProject,
  ],
  (
    isAuthenticated,
    hasPendingAuth,
    requiresOrgSelection,
    requiresProjSelection,
    selectedOrg,
    selectedProj
  ): "unauthenticated" | "select-organization" | "select-project" | "complete" => {
    // If we have pending auth but not yet authenticated, we're in org selection
    if (hasPendingAuth && !isAuthenticated) return "select-organization";
    if (!isAuthenticated && !hasPendingAuth) return "unauthenticated";
    if (requiresOrgSelection || !selectedOrg) return "select-organization";
    if (requiresProjSelection || !selectedProj) return "select-project";
    return "complete";
  }
);

// Derived CIN selector - prefers selected organization CIN over login response CIN
export const selectActiveCIN = createSelector(
  [selectSelectedOrganizationCIN, selectCIN],
  (selectedOrgCIN, loginCIN) => selectedOrgCIN || loginCIN
);

// ============================================
// Auth Transition Loader Selectors (Optimized Login Flow)
// ============================================

export const selectAuthTransitionLoading = createSelector(
  userDetails,
  (auth) => auth.authTransitionLoading
);

export const selectAuthTransitionMessage = createSelector(
  userDetails,
  (auth) => auth.authTransitionMessage
);

// ============================================
// Project Token Claims Selectors
// ============================================

/** Full decoded project token claims object */
export const selectProjectTokenClaims = createSelector(
  userDetails,
  (auth) => auth.projectTokenClaims
);

/** Whether the current user is a super admin (from project token) */
export const selectIsSuperAdmin = createSelector(
  [selectProjectTokenClaims, userDetails],
  (claims, auth) => {
    if (auth.rolePermissions?.data) {
      return auth.rolePermissions.superAdmin === true;
    }
    return claims?.superAdmin === true;
  }
);

/** User role from the project token (e.g. "Super Admin", "Admin", "User") */
export const selectProjectRole = createSelector(
  selectProjectTokenClaims,
  (claims) => claims?.role ?? null
);

/** Agent ID from the project token */
export const selectProjectAgentId = createSelector(
  selectProjectTokenClaims,
  (claims) => claims?.agentId ?? null
);

/** Project ID from the project token */
export const selectProjectTokenProjectId = createSelector(
  selectProjectTokenClaims,
  (claims) => claims?.projectId ?? null
);

/** CIN from the project token */
export const selectProjectTokenCIN = createSelector(
  selectProjectTokenClaims,
  (claims) => claims?.cin ?? null
);

/** Token type from the project token (e.g. "PROJECT") */
export const selectProjectTokenType = createSelector(
  selectProjectTokenClaims,
  (claims) => claims?.type ?? null
);

/** Email from the project token */
export const selectProjectTokenEmail = createSelector(
  selectProjectTokenClaims,
  (claims) => claims?.email ?? null
);

/** Whether the project token has expired */
export const selectIsProjectTokenExpired = createSelector(
  selectProjectTokenClaims,
  (claims) => {
    if (!claims?.expiresAt) return null;
    return claims.expiresAt * 1000 < Date.now();
  }
);

// ============================================
// Runtime Role Permission Selectors
// ============================================

export const selectRuntimeRolePermissionsState = createSelector(
  userDetails,
  (auth) => auth.rolePermissions
);

export const selectAuthCurrentSubscription = createSelector(
  userDetails,
  (auth) => auth.currentSubscription
);

export const selectRuntimeRolePermissions = createSelector(
  selectRuntimeRolePermissionsState,
  (rolePermissions) => rolePermissions.data
);

export const selectRuntimeRolePermissionKeys = createSelector(
  selectRuntimeRolePermissionsState,
  (rolePermissions) => rolePermissions.permissions
);

export const selectRuntimeRolePermissionMap = createSelector(
  selectRuntimeRolePermissionsState,
  (rolePermissions) => rolePermissions.byKey
);

export const selectRuntimeRolePermissionsRoleId = createSelector(
  selectRuntimeRolePermissionsState,
  (rolePermissions) => rolePermissions.roleId
);

export const selectRuntimeRolePermissionsProjectId = createSelector(
  selectRuntimeRolePermissionsState,
  (rolePermissions) => rolePermissions.projectId
);

export const selectRuntimeRolePermissionsCIN = createSelector(
  selectRuntimeRolePermissionsState,
  (rolePermissions) => rolePermissions.cin
);

export const selectRuntimeRolePermissionsLoading = createSelector(
  selectRuntimeRolePermissionsState,
  (rolePermissions) => rolePermissions.isLoading
);

export const selectRuntimeRolePermissionsError = createSelector(
  selectRuntimeRolePermissionsState,
  (rolePermissions) => rolePermissions.error
);

export const selectRuntimeRolePermissionsLastFetchedAt = createSelector(
  selectRuntimeRolePermissionsState,
  (rolePermissions) => rolePermissions.lastFetchedAt
);

export const selectRuntimeRolePermissionContextMatches = createSelector(
  [
    selectRuntimeRolePermissionsProjectId,
    selectRuntimeRolePermissionsCIN,
    selectSelectedProjectId,
    selectActiveCIN,
  ],
  (loadedProjectId, loadedCin, selectedProjectId, activeCin) =>
    Boolean(selectedProjectId && activeCin) &&
    loadedProjectId === selectedProjectId &&
    loadedCin === activeCin
);

export const selectRuntimeRolePermissionsReady = createSelector(
  [
    selectRuntimeRolePermissionContextMatches,
    selectRuntimeRolePermissionsState,
  ],
  (contextMatches, rolePermissions) =>
    contextMatches &&
    !rolePermissions.isLoading &&
    (rolePermissions.data !== null || rolePermissions.error !== null)
);

export const selectRuntimeRoleHasAllPermissions = createSelector(
  selectRuntimeRolePermissionsState,
  (rolePermissions) =>
    rolePermissions.hasAllPermissions === true ||
    rolePermissions.superAdmin === true
);

export const selectHasRuntimeRolePermission = (permissionKey: string) =>
  createSelector(
    [selectRuntimeRolePermissionMap, selectIsSuperAdmin, selectRuntimeRoleHasAllPermissions],
    (permissionMap, isSuperAdmin, hasAllPermissions) =>
      isSuperAdmin ||
      hasAllPermissions ||
      permissionMap["*"] === true ||
      permissionMap[permissionKey] === true
  );
